import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query
} from "firebase/firestore";

const STORE_TTL_MS = 30000;

const createEmptyData = () => ({
  tasks: [],
  kids: [],
  history: [],
  calendarEvents: [],
  familySettings: null,
  rewards: [],
  redemptions: [],
  auditLogs: [],
  members: []
});

const cloneData = (data) => ({
  ...data,
  tasks: [...(data.tasks || [])],
  kids: [...(data.kids || [])],
  history: [...(data.history || [])],
  calendarEvents: [...(data.calendarEvents || [])],
  rewards: [...(data.rewards || [])],
  redemptions: [...(data.redemptions || [])],
  auditLogs: [...(data.auditLogs || [])],
  members: [...(data.members || [])]
});

const familyStores = new Map();

const notifyStore = (store) => {
  const snapshot = cloneData(store.data);
  store.subscribers.forEach((subscriber) => {
    subscriber(snapshot);
  });
};

const stopBaseListeners = (store) => {
  store.baseUnsubs.forEach((unsub) => unsub());
  store.baseUnsubs = [];
  store.baseStarted = false;
};

const stopManageListeners = (store) => {
  store.manageUnsubs.forEach((unsub) => unsub());
  store.manageUnsubs = [];
  store.manageStarted = false;
  store.data = { ...store.data, auditLogs: [], members: [] };
  notifyStore(store);
};

const startBaseListeners = (store) => {
  if (store.baseStarted) return;
  store.baseStarted = true;

  const familyId = store.familyId;

  const unsubFamily = onSnapshot(
    doc(db, "families", familyId),
    (snap) => {
      store.data = {
        ...store.data,
        familySettings: snap.exists() ? snap.data() : null
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, familySettings: null };
      notifyStore(store);
    }
  );

  const unsubTasks = onSnapshot(
    collection(db, "families", familyId, "tasks"),
    (snap) => {
      store.data = {
        ...store.data,
        tasks: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, tasks: [] };
      notifyStore(store);
    }
  );

  const unsubKids = onSnapshot(
    collection(db, "families", familyId, "kids"),
    (snap) => {
      store.data = {
        ...store.data,
        kids: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, kids: [] };
      notifyStore(store);
    }
  );

  const unsubHistory = onSnapshot(
    query(collection(db, "families", familyId, "history"), orderBy("createdAt", "desc"), limit(200)),
    (snap) => {
      store.data = {
        ...store.data,
        history: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, history: [] };
      notifyStore(store);
    }
  );

  const unsubCalendar = onSnapshot(
    collection(db, "families", familyId, "calendarEvents"),
    (snap) => {
      store.data = {
        ...store.data,
        calendarEvents: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, calendarEvents: [] };
      notifyStore(store);
    }
  );

  const unsubRewards = onSnapshot(
    collection(db, "families", familyId, "rewards"),
    (snap) => {
      store.data = {
        ...store.data,
        rewards: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, rewards: [] };
      notifyStore(store);
    }
  );

  const unsubRedemptions = onSnapshot(
    collection(db, "families", familyId, "redemptions"),
    (snap) => {
      store.data = {
        ...store.data,
        redemptions: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, redemptions: [] };
      notifyStore(store);
    }
  );

  store.baseUnsubs = [
    unsubFamily,
    unsubTasks,
    unsubKids,
    unsubHistory,
    unsubCalendar,
    unsubRewards,
    unsubRedemptions
  ];
};

const startManageListeners = (store) => {
  if (store.manageStarted) return;
  store.manageStarted = true;

  const familyId = store.familyId;

  const unsubAudit = onSnapshot(
    query(collection(db, "families", familyId, "audit"), orderBy("createdAt", "desc"), limit(200)),
    (snap) => {
      store.data = {
        ...store.data,
        auditLogs: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, auditLogs: [] };
      notifyStore(store);
    }
  );

  const unsubMembers = onSnapshot(
    collection(db, "families", familyId, "members"),
    (snap) => {
      store.data = {
        ...store.data,
        members: snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      };
      notifyStore(store);
    },
    () => {
      store.data = { ...store.data, members: [] };
      notifyStore(store);
    }
  );

  store.manageUnsubs = [unsubAudit, unsubMembers];
};

const ensureStore = (familyId) => {
  if (familyStores.has(familyId)) return familyStores.get(familyId);

  const store = {
    familyId,
    data: createEmptyData(),
    subscribers: new Set(),
    baseUnsubs: [],
    manageUnsubs: [],
    baseStarted: false,
    manageStarted: false,
    manageSubscribers: 0,
    cleanupTimer: null
  };

  familyStores.set(familyId, store);
  return store;
};

const clearCleanupTimer = (store) => {
  if (!store.cleanupTimer) return;
  clearTimeout(store.cleanupTimer);
  store.cleanupTimer = null;
};

const scheduleCleanup = (store) => {
  clearCleanupTimer(store);
  store.cleanupTimer = setTimeout(() => {
    if (store.subscribers.size > 0) return;
    stopManageListeners(store);
    stopBaseListeners(store);
    familyStores.delete(store.familyId);
    clearCleanupTimer(store);
  }, STORE_TTL_MS);
};

export const useFamilyData = (familyId, options = {}) => {
  const canManage = options?.canManage === true;
  const [state, setState] = useState(createEmptyData);

  useEffect(() => {
    if (!familyId) {
      setState(createEmptyData());
      return undefined;
    }

    const store = ensureStore(familyId);
    clearCleanupTimer(store);
    startBaseListeners(store);

    if (canManage) {
      store.manageSubscribers += 1;
      startManageListeners(store);
    }

    const subscriber = (nextData) => {
      setState(nextData);
    };

    store.subscribers.add(subscriber);
    subscriber(cloneData(store.data));

    return () => {
      store.subscribers.delete(subscriber);
      if (canManage) {
        store.manageSubscribers = Math.max(0, store.manageSubscribers - 1);
        if (store.manageSubscribers === 0) {
          stopManageListeners(store);
        }
      }

      if (store.subscribers.size === 0) {
        scheduleCleanup(store);
      }
    };
  }, [familyId, canManage]);

  return state;
};
